import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { SECONDARY_MUSCLES, ALL_SECONDARY_MUSCLES } from './constants/exerciseCategories';
import {
  SET_CATEGORIES,
  getBlockTypeForCategory,
  getMinExercisesForCategory,
  getMaxExercisesForCategory,
  getDefaultParamsForCategory,
  getPositionLabel,
} from './constants/blockTypes';
import BlockTypeSelector from './components/blocks/BlockTypeSelector';
import BlockConfigPanel from './components/blocks/BlockConfigPanel';
import { toastSuccess, toastError, toastCascadeFailed } from './ui/toast';
import CipherHeader from './components/ui/CipherHeader';
import {
  getActiveAssignmentCurrentWeek,
  cascadeToForwardBlocks,
} from './services/programCascadeService';
import { getDefaultPrescription } from './utils/exerciseDefaults';
import { assertWriteOk as must } from './utils/dbWrite';
import ExerciseDemo from './components/ui/ExerciseDemo';
import { colors as theme } from './theme';

const brandColors = {
  gold: theme.gold,
  darkGray: theme.bg,
  mediumGray: theme.surface1,
  lightGray: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
};

const DRAFT_KEY = 'cipher_program_builder_draft';

const ProgramBuilder = ({ onBack, programId = null, activeClientsCount = 0 }) => {
  const [programName, setProgramName] = useState('');
  const [programDescription, setProgramDescription] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [expandedBlock, setExpandedBlock] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingProgram, setLoadingProgram] = useState(false);

  // Exercise picker state
  const [showExercisePicker, setShowExercisePicker] = useState(null); // { weekId, dayId, blockId }
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [selectedMuscleFilter, setSelectedMuscleFilter] = useState('All');
  const [selectedSecondaryFilter, setSelectedSecondaryFilter] = useState('All');
  const [hoveredExerciseId, setHoveredExerciseId] = useState(null);

  useEffect(() => {
    loadExercises();
    // Check for a saved draft on mount
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) setHasDraft(true);
    } catch {}
  }, []);

  // Auto-save draft whenever form changes (skip when form is blank, skip in edit mode)
  useEffect(() => {
    if (isEditMode) return;
    if (!programName && weeks.length === 0) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ programName, programDescription, weeks }));
    } catch {}
  }, [programName, programDescription, weeks, isEditMode]);

  const restoreDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const { programName: n, programDescription: d, weeks: w } = JSON.parse(raw);
      setProgramName(n || '');
      setProgramDescription(d || '');
      setWeeks(w || []);
      setHasDraft(false);
      toastSuccess('Draft restored!');
    } catch {
      toastError('Could not restore draft.');
    }
  };

  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setHasDraft(false);
  };

  useEffect(() => {
    if (programId) {
      setIsEditMode(true);
      loadProgramForEdit(programId);
    }
  }, [programId]);

  const loadExercises = async () => {
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select('*')
        .order('name');

      if (error) throw error;
      setExercises(data || []);
    } catch (err) {
      console.error('Error loading exercises:', err);
      setError('Failed to load exercises');
    }
  };

  const loadProgramForEdit = async (id) => {
    setLoadingProgram(true);
    try {
      // Load program record
      const { data: prog } = await supabase
        .from('programs')
        .select('name, description')
        .eq('id', id)
        .single();
      setProgramName(prog.name || '');
      setProgramDescription(prog.description || '');

      // Load full structure
      const { data: dbWeeks } = await supabase
        .from('program_weeks')
        .select(`
          id, name, week_number,
          program_days (
            id, name, day_number,
            exercise_blocks (
              id, block_order, block_type, set_category, category_params, rest_seconds,
              block_items (
                id, exercise_id, position, sets, reps, load_percentage, notes,
                prescribed_weight, bodyweight_multiplier
              )
            )
          )
        `)
        .eq('program_id', id)
        .order('week_number');

      // Map to builder state — store _dbId so update knows which records exist
      const mappedWeeks = (dbWeeks || []).map(w => ({
        id: w.id,           // use real DB id as React key
        _dbId: w.id,        // flag: this came from DB
        name: w.name,
        days: (w.program_days || [])
          .sort((a, b) => a.day_number - b.day_number)
          .map(d => ({
            id: d.id,
            _dbId: d.id,
            name: d.name,
            blocks: (d.exercise_blocks || [])
              .sort((a, b) => a.block_order - b.block_order)
              .map(b => ({
                id: b.id,
                _dbId: b.id,
                block_order: b.block_order,
                block_type: b.block_type,
                set_category: b.set_category,
                category_params: b.category_params || {},
                rest_seconds: b.rest_seconds,
                items: (b.block_items || [])
                  .sort((a, b) => a.position - b.position)
                  .map(i => ({
                    id: i.id,
                    _dbId: i.id,
                    exercise_id: i.exercise_id,
                    position: i.position,
                    sets: i.sets,
                    reps: i.reps,
                    load_percentage: i.load_percentage,
                    notes: i.notes || '',
                    prescribed_weight: i.prescribed_weight,
                    bodyweight_multiplier: i.bodyweight_multiplier,
                  })),
              })),
          })),
      }));

      setWeeks(mappedWeeks);
    } catch (err) {
      console.error('Error loading program for edit:', err);
      setError('Failed to load program for editing');
    } finally {
      setLoadingProgram(false);
    }
  };

  const addWeek = () => {
    const newWeek = {
      id: Date.now(),
      name: `Week ${weeks.length + 1}`,
      days: []
    };
    setWeeks([...weeks, newWeek]);
    setExpandedWeek(newWeek.id);
  };

  // eslint-disable-next-line no-unused-vars
  const updateWeek = (weekId, field, value) => {
    setWeeks(weeks.map(week =>
      week.id === weekId ? { ...week, [field]: value } : week
    ));
  };

  const removeWeek = (weekId) => {
    setWeeks(weeks.filter(week => week.id !== weekId));
  };

  const updateDay = (weekId, dayId, field, value) => {
    setWeeks(weeks.map(week => {
      if (week.id !== weekId) return week;
      return {
        ...week,
        days: week.days.map(day =>
          day.id === dayId ? { ...day, [field]: value } : day
        ),
      };
    }));
  };

  const BLOCK_PRESETS = {
    fatloss:     { label: 'Fat Loss',    sets: 3, reps: '12-15', rest: 45 },
    hypertrophy: { label: 'Hypertrophy', sets: 4, reps: '10-12', rest: 60 },
    strength:    { label: 'Strength',    sets: 5, reps: '4-6',   rest: 90 },
    endurance:   { label: 'Endurance',   sets: 3, reps: '15-20', rest: 30 },
  };

  const applyPresetToBlock = (weekId, dayId, blockId, presetKey) => {
    const p = BLOCK_PRESETS[presetKey];
    if (!p) return;
    setWeeks(weeks.map(week => {
      if (week.id !== weekId) return week;
      return {
        ...week,
        days: week.days.map(day => {
          if (day.id !== dayId) return day;
          return {
            ...day,
            blocks: day.blocks.map(block => {
              if (block.id !== blockId) return block;
              return {
                ...block,
                rest_seconds: p.rest,
                items: block.items.map(item => ({ ...item, sets: p.sets, reps: p.reps })),
              };
            }),
          };
        }),
      };
    }));
  };

  const addDay = (weekId) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        const newDay = {
          id: Date.now(),
          name: `Day ${week.days.length + 1}`,
          blocks: []
        };
        return { ...week, days: [...week.days, newDay] };
      }
      return week;
    }));
  };

  const removeDay = (weekId, dayId) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.filter(day => day.id !== dayId)
        };
      }
      return week;
    }));
  };

  const addBlock = (weekId, dayId) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.map(day => {
            if (day.id === dayId) {
              const newBlock = {
                id: Date.now(),
                block_order: day.blocks.length + 1,
                block_type: 'single',
                set_category: 'normal',
                category_params: {},
                rest_seconds: 60,
                items: []
              };
              setExpandedBlock(newBlock.id);
              return { ...day, blocks: [...day.blocks, newBlock] };
            }
            return day;
          })
        };
      }
      return week;
    }));
  };

  const updateBlock = (weekId, dayId, blockId, field, value) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.map(day => {
            if (day.id === dayId) {
              return {
                ...day,
                blocks: day.blocks.map(block => {
                  if (block.id === blockId) {
                    const updated = { ...block, [field]: value };
                    // Update block_type when category changes
                    if (field === 'set_category') {
                      updated.block_type = getBlockTypeForCategory(value);
                      updated.category_params = getDefaultParamsForCategory(value);
                    }
                    return updated;
                  }
                  return block;
                })
              };
            }
            return day;
          })
        };
      }
      return week;
    }));
  };

  const removeBlock = (weekId, dayId, blockId) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.map(day => {
            if (day.id === dayId) {
              return {
                ...day,
                blocks: day.blocks.filter(block => block.id !== blockId)
              };
            }
            return day;
          })
        };
      }
      return week;
    }));
  };

  const addExerciseToBlock = (weekId, dayId, blockId, exerciseId) => {
    const exercise = exercises.find(e => e.id === exerciseId);
    if (!exercise) return;

    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.map(day => {
            if (day.id === dayId) {
              return {
                ...day,
                blocks: day.blocks.map(block => {
                  if (block.id === blockId) {
                    const { sets: defaultSets, reps: defaultReps } = getDefaultPrescription(exercise);
                    const newItem = {
                      id: Date.now(),
                      exercise_id: exerciseId,
                      exercise_name: exercise.name,
                      movement_category: exercise.movement_category,
                      position: block.items.length + 1,
                      sets: defaultSets,
                      reps: defaultReps,
                      load_percentage: '',
                      notes: '',
                    };
                    return { ...block, items: [...block.items, newItem] };
                  }
                  return block;
                })
              };
            }
            return day;
          })
        };
      }
      return week;
    }));

    setShowExercisePicker(null);
    setExerciseSearch('');
    setSelectedSecondaryFilter('All');
  };

  const updateExerciseDurationUnit = async (exerciseId, unit) => {
    setExercises(prev => prev.map(e => e.id === exerciseId ? { ...e, duration_unit: unit } : e));
    await supabase.from('exercises').update({ duration_unit: unit }).eq('id', exerciseId);
  };

  const updateBlockItem = (weekId, dayId, blockId, itemId, field, value) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.map(day => {
            if (day.id === dayId) {
              return {
                ...day,
                blocks: day.blocks.map(block => {
                  if (block.id === blockId) {
                    return {
                      ...block,
                      items: block.items.map(item =>
                        item.id === itemId ? { ...item, [field]: value } : item
                      )
                    };
                  }
                  return block;
                })
              };
            }
            return day;
          })
        };
      }
      return week;
    }));
  };

  const removeBlockItem = (weekId, dayId, blockId, itemId) => {
    setWeeks(weeks.map(week => {
      if (week.id === weekId) {
        return {
          ...week,
          days: week.days.map(day => {
            if (day.id === dayId) {
              return {
                ...day,
                blocks: day.blocks.map(block => {
                  if (block.id === blockId) {
                    const filtered = block.items.filter(item => item.id !== itemId);
                    const reordered = filtered.map((item, idx) => ({ ...item, position: idx + 1 }));
                    return { ...block, items: reordered };
                  }
                  return block;
                })
              };
            }
            return day;
          })
        };
      }
      return week;
    }));
  };

  const getMovementCategories = () => {
    const categories = new Set(exercises.map(e => e.movement_category).filter(Boolean));
    return ['All', ...Array.from(categories).sort()];
  };

  const getSecondaryMuscles = () => {
    if (selectedMuscleFilter === 'All') {
      return ['All', ...ALL_SECONDARY_MUSCLES];
    }
    return ['All', ...(SECONDARY_MUSCLES[selectedMuscleFilter] || [])];
  };

  const getFilteredExercises = () => {
    return exercises.filter(ex => {
      const matchesSearch = ex.name.toLowerCase().includes(exerciseSearch.toLowerCase());
      const matchesCategory = selectedMuscleFilter === 'All' || ex.movement_category === selectedMuscleFilter;
      const matchesSecondary = selectedSecondaryFilter === 'All' ||
        (ex.secondary_muscles && ex.secondary_muscles.includes(selectedSecondaryFilter));
      return matchesSearch && matchesCategory && matchesSecondary;
    });
  };

  const saveProgram = async () => {
    if (!programName.trim()) {
      setError('Please enter a program name');
      return;
    }

    if (weeks.length === 0) {
      setError('Please add at least one week');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Checked rather than left to crash on user.id: getUser() is a network call and returns
      // user: null when it fails. Without this the coach saw "Cannot read properties of null"
      // instead of being told the save did not happen. (The clone path had the same hole and a
      // worse outcome — see programCloneService.)
      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (authErr || !user?.id) {
        throw new Error(
          'Could not confirm who you are signed in as, so the program was not saved. ' +
          'Check your connection and try again.'
        );
      }

      const { data: program, error: programError } = await supabase
        .from('programs')
        .insert({
          name: programName,
          description: programDescription,
          user_id: isTemplate ? null : user.id,
          created_by: user.id,
          uses_blocks: true,
          is_template: isTemplate,
          is_client_copy: false,
        })
        .select()
        .single();

      if (programError) {
        throw programError;
      }

      // Save weeks
      for (const week of weeks) {
        const { data: savedWeek, error: weekError } = await supabase
          .from('program_weeks')
          .insert({
            program_id: program.id,
            name: week.name,
            week_number: weeks.indexOf(week) + 1
          })
          .select()
          .single();

        if (weekError) {
          throw weekError;
        }

        for (const day of week.days) {
          const { data: savedDay, error: dayError } = await supabase
            .from('program_days')
            .insert({
              week_id: savedWeek.id,
              name: day.name,
              day_number: week.days.indexOf(day) + 1
            })
            .select()
            .single();

          if (dayError) {
            throw dayError;
          }

          // Save blocks
          for (const block of day.blocks) {
            const { data: savedBlock, error: blockError } = await supabase
              .from('exercise_blocks')
              .insert({
                day_id: savedDay.id,
                block_order: block.block_order,
                block_type: block.block_type,
                set_category: block.set_category,
                category_params: block.category_params,
                rest_seconds: block.rest_seconds,
              })
              .select()
              .single();

            if (blockError) {
              throw blockError;
            }

            // Save block items
            for (const item of block.items) {
              const { error: itemError } = await supabase
                .from('block_items')
                .insert({
                  block_id: savedBlock.id,
                  exercise_id: item.exercise_id,
                  position: item.position,
                  sets: item.sets,
                  reps: item.reps,
                  load_percentage: item.load_percentage || null,
                  notes: item.notes,
                });

              if (itemError) {
                throw itemError;
              }
            }
          }
        }
      }

      toastSuccess('Program saved successfully!');

      // Clear draft and reset form
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      setProgramName('');
      setProgramDescription('');
      setWeeks([]);
      setExpandedWeek(null);
      setExpandedDay(null);
      setExpandedBlock(null);

    } catch (err) {
      console.error('Error saving program:', err);
      setError('Error saving program: ' + err.message);
      toastError('Error saving program');
    } finally {
      setSaving(false);
    }
  };

  const updateProgram = async () => {
    if (!programName.trim()) { setError('Please enter a program name'); return; }
    if (weeks.length === 0) { setError('Please add at least one week'); return; }

    setSaving(true);
    setError(null);

    try {
      // Every write below is wrapped in must() (assertWriteOk). supabase-js RESOLVES with
      // { error } rather than throwing, so these all used to be silent no-ops that still finished
      // with "Program updated successfully!" — the same class as the coach-editor cascade bug.
      // Worse for the inserts: `const { data: nw } = ...` then `nw.id` crashed with "Cannot
      // read properties of null" instead of naming the failure.
      //
      // This is a long sequence of separate requests with NO transaction, so it cannot be made
      // all-or-nothing here. Stopping at the first failure and saying the program is only partly
      // saved is the honest option; making it atomic needs a database function like 057.
      // 1. Update program record
      must('Saving program details', await supabase
        .from('programs')
        .update({ name: programName, description: programDescription })
        .eq('id', programId));

      // 1b. If this is an already-assigned client program (not an unassigned master
      // template), fetch each existing item's current DB values so item edits below can be
      // diffed and cascaded forward to the client's current + already-generated future weeks
      // — same pattern ClientManagement.jsx's Programs tab editor already uses, since without
      // it an edit here would only ever land on whatever week happens to be loaded, silently
      // never reaching a client who has already progressed past it.
      const existingItemIds = [];
      for (const week of weeks) {
        for (const day of week.days) {
          for (const block of day.blocks) {
            for (const item of block.items) {
              if (item._dbId) existingItemIds.push(item._dbId);
            }
          }
        }
      }
      let cascadeWeek = null;
      const oldItemsMap = new Map();
      if (existingItemIds.length > 0) {
        // Both reads throw on failure so the save stops here, before any structural write. If a
        // failed read were treated as "no assignment / no old values" the save would go ahead
        // with no forward cascade at all — and, since the diff below is taken against what's in
        // the database, a re-save afterwards could never recover the edits that didn't cascade.
        cascadeWeek = await getActiveAssignmentCurrentWeek(programId);
        if (cascadeWeek != null) {
          const { data: oldItems, error: oldItemsErr } = await supabase
            .from('block_items')
            .select('id, exercise_id, sets, reps, load_percentage, notes, prescribed_weight, bodyweight_multiplier')
            .in('id', existingItemIds);
          if (oldItemsErr) throw oldItemsErr;
          (oldItems || []).forEach(oi => oldItemsMap.set(oi.id, oi));
        }
      }

      // Forward cascades that failed after their item's own update had landed. Collected (rather
      // than thrown) so the rest of the save still completes; reported with a Retry at the end.
      let failedCascades = [];

      // 2. Track which DB IDs are still present in new state
      const keptWeekDbIds = new Set();
      const keptDayDbIds = new Set();
      const keptBlockDbIds = new Set();
      const keptItemDbIds = new Set();

      for (const [wi, week] of weeks.entries()) {
        let weekDbId = week._dbId;
        if (weekDbId) {
          // UPDATE existing week
          must('Saving week ' + (wi + 1), await supabase.from('program_weeks')
            .update({ name: week.name, week_number: wi + 1 })
            .eq('id', weekDbId));
          keptWeekDbIds.add(weekDbId);
        } else {
          // INSERT new week
          const nw = must('Creating week ' + (wi + 1), await supabase.from('program_weeks')
            .insert({ program_id: programId, name: week.name, week_number: wi + 1 })
            .select('id').single());
          weekDbId = nw.id;
          keptWeekDbIds.add(weekDbId);
        }

        for (const [di, day] of week.days.entries()) {
          let dayDbId = day._dbId;
          if (dayDbId) {
            must('Saving day ' + (di + 1), await supabase.from('program_days')
              .update({ name: day.name, day_number: di + 1, week_id: weekDbId })
              .eq('id', dayDbId));
            keptDayDbIds.add(dayDbId);
          } else {
            const nd = must('Creating day ' + (di + 1), await supabase.from('program_days')
              .insert({ week_id: weekDbId, name: day.name, day_number: di + 1 })
              .select('id').single());
            dayDbId = nd.id;
            keptDayDbIds.add(dayDbId);
          }

          for (const [bi, block] of day.blocks.entries()) {
            let blockDbId = block._dbId;
            if (blockDbId) {
              must('Saving block ' + (bi + 1), await supabase.from('exercise_blocks')
                .update({
                  block_order: bi + 1,
                  block_type: block.block_type,
                  set_category: block.set_category,
                  category_params: block.category_params,
                  rest_seconds: block.rest_seconds,
                  })
                .eq('id', blockDbId));
              keptBlockDbIds.add(blockDbId);
            } else {
              const nb = must('Creating block ' + (bi + 1), await supabase.from('exercise_blocks')
                .insert({
                  day_id: dayDbId,
                  block_order: bi + 1,
                  block_type: block.block_type,
                  set_category: block.set_category,
                  category_params: block.category_params,
                  rest_seconds: block.rest_seconds,
                  })
                .select('id').single());
              blockDbId = nb.id;
              keptBlockDbIds.add(blockDbId);
            }

            for (const [ii, item] of block.items.entries()) {
              if (item._dbId) {
                const newValues = {
                  exercise_id: item.exercise_id,
                  position: ii + 1,
                  sets: item.sets,
                  reps: item.reps,
                  load_percentage: item.load_percentage || null,
                  notes: item.notes,
                  prescribed_weight: item.prescribed_weight || null,
                  bodyweight_multiplier: item.bodyweight_multiplier || null,
                };
                must('Saving exercise ' + (ii + 1), await supabase.from('block_items')
                  .update(newValues)
                  .eq('id', item._dbId));
                keptItemDbIds.add(item._dbId);

                // Cascade any actually-changed fields forward to the same block position
                // in the client's current + already-generated future weeks.
                const oldItem = cascadeWeek != null ? oldItemsMap.get(item._dbId) : null;
                if (oldItem) {
                  const cascadeKeys = ['exercise_id', 'sets', 'reps', 'load_percentage', 'notes', 'prescribed_weight', 'bodyweight_multiplier'];
                  const changedFields = {};
                  for (const key of cascadeKeys) {
                    if ((oldItem[key] ?? null) !== (newValues[key] ?? null)) {
                      changedFields[key] = newValues[key];
                    }
                  }
                  if (Object.keys(changedFields).length > 0) {
                    // Repeating is safe: the write only matches rows still holding the old exercise.
                    const attemptCascade = () => cascadeToForwardBlocks({
                      programId,
                      currentWeek: cascadeWeek,
                      dayNumber: di + 1,
                      blockOrder: bi + 1,
                      write: (blockIds) => supabase.from('block_items')
                        .update(changedFields)
                        .eq('exercise_id', oldItem.exercise_id)
                        .in('block_id', blockIds),
                    });
                    const cascade = await attemptCascade();
                    if (!cascade.ok) {
                      console.error('Forward-week cascade failed:', cascade.error);
                      failedCascades.push(attemptCascade);
                    }
                  }
                }
              } else {
                const ni = must('Creating exercise ' + (ii + 1), await supabase.from('block_items')
                  .insert({
                    block_id: blockDbId,
                    exercise_id: item.exercise_id,
                    position: ii + 1,
                    sets: item.sets,
                    reps: item.reps,
                    load_percentage: item.load_percentage || null,
                    notes: item.notes,
                    prescribed_weight: item.prescribed_weight || null,
                    bodyweight_multiplier: item.bodyweight_multiplier || null,
                  })
                  .select('id').single());
                keptItemDbIds.add(ni.id);
              }
            }
          }
        }
      }

      // 3. Delete removed records (those with _dbId not in kept sets)
      // Get all current DB ids for this program
      // A failed read here would look like "nothing to remove" and silently skip the deletes.
      const allWeeks = must('Reading weeks', await supabase.from('program_weeks').select('id').eq('program_id', programId));
      const removedWeekIds = (allWeeks || []).map(w => w.id).filter(id => !keptWeekDbIds.has(id));
      if (removedWeekIds.length > 0) {
        // Cascade: days → blocks → items deleted by FK ON DELETE CASCADE
        must('Removing deleted weeks', await supabase.from('program_weeks').delete().in('id', removedWeekIds));
      }

      // Get all current day ids in kept weeks
      if (keptDayDbIds.size > 0 || keptWeekDbIds.size > 0) {
        const allDays = must('Reading days', await supabase.from('program_days')
          .select('id').in('week_id', [...keptWeekDbIds]));
        const removedDayIds = (allDays || []).map(d => d.id).filter(id => !keptDayDbIds.has(id));
        if (removedDayIds.length > 0) {
          must('Removing deleted days', await supabase.from('program_days').delete().in('id', removedDayIds));
        }
      }

      // Get all current block ids in kept days
      if (keptDayDbIds.size > 0) {
        const allBlocks = must('Reading blocks', await supabase.from('exercise_blocks')
          .select('id').in('day_id', [...keptDayDbIds]));
        const removedBlockIds = (allBlocks || []).map(b => b.id).filter(id => !keptBlockDbIds.has(id));
        if (removedBlockIds.length > 0) {
          must('Removing deleted blocks', await supabase.from('exercise_blocks').delete().in('id', removedBlockIds));
        }
      }

      // Get all current item ids in kept blocks
      if (keptBlockDbIds.size > 0) {
        const allItems = must('Reading exercises', await supabase.from('block_items')
          .select('id').in('block_id', [...keptBlockDbIds]));
        const removedItemIds = (allItems || []).map(i => i.id).filter(id => !keptItemDbIds.has(id));
        if (removedItemIds.length > 0) {
          must('Removing deleted exercises', await supabase.from('block_items').delete().in('id', removedItemIds));
        }
      }

      if (failedCascades.length === 0) {
        toastSuccess('Program updated successfully!');
      } else {
        const count = failedCascades.length;
        toastCascadeFailed(
          `Program updated, but ${count} change${count === 1 ? '' : 's'} didn't reach the client's current and later weeks.`,
          async () => {
            // Only the cascades still failing are retried on each attempt.
            const stillFailing = [];
            for (const attempt of failedCascades) {
              const res = await attempt();
              if (!res.ok) {
                console.error('Forward-week cascade retry failed:', res.error);
                stillFailing.push(attempt);
              }
            }
            failedCascades = stillFailing;
            return stillFailing.length === 0;
          }
        );
      }
      onBack?.();
    } catch (err) {
      console.error('Error updating program:', err);
      // A write failing part-way leaves the program HALF SAVED — there is no transaction around
      // this sequence. Saying so is the difference between a coach re-opening the editor to check
      // and one walking away believing nothing changed.
      if (err.partialSave) {
        setError(
          `${err.message}. The program is only partly saved — re-open it, check it looks right, ` +
          'and save again.'
        );
        toastError('Save incomplete — the program is only partly saved');
      } else {
        setError('Error updating program: ' + err.message);
        toastError('Error updating program');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      padding: '40px',
      backgroundColor: brandColors.darkGray,
      minHeight: '100vh'
    }}>
      <CipherHeader
        title={isEditMode ? 'Edit Program' : 'Program Builder'}
        subtitle={isEditMode ? programName : 'Week 1 template — further weeks auto-generate on completion'}
        onBack={onBack}
        backLabel="Admin Panel"
      />

      {/* Sticky Save Bar: the one Save, always in reach. Add Week lives with the weeks below.
          (15 October 2026: Save and Add Week each appeared twice, and Save stretched the full width.) */}
      {!loadingProgram && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          backgroundColor: brandColors.darkGray,
          borderBottom: `1px solid ${brandColors.lightGray}`,
          padding: '10px 0 12px',
          marginBottom: 16,
          display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end',
        }}>
          <span style={{ flex: 1, minWidth: 0, color: brandColors.silver, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {weeks.length === 0 ? 'No weeks yet' : `${weeks.length} ${weeks.length === 1 ? 'week' : 'weeks'}`}
          </span>
          <button
            onClick={isEditMode ? updateProgram : saveProgram}
            disabled={saving}
            style={{
              padding: '12px 28px',
              backgroundColor: saving ? brandColors.lightGray : brandColors.gold,
              border: 'none', borderRadius: 8,
              color: saving ? brandColors.silver : brandColors.darkGray,
              fontSize: 15, fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : isEditMode ? 'Update Program' : 'Save Program'}
          </button>
        </div>
      )}

      {loadingProgram && (
        <div style={{ textAlign: 'center', padding: '40px', color: brandColors.silver }}>
          Loading program...
        </div>
      )}

      {isEditMode && activeClientsCount > 0 && (
        <div style={{
          background: 'rgba(255, 149, 0, 0.1)',
          border: `1px solid ${theme.warning}`,
          borderRadius: '14px',
          padding: '14px 18px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <p style={{ color: theme.warning, fontWeight: 700, margin: '0 0 4px', fontSize: 14 }}>
              {activeClientsCount} client{activeClientsCount !== 1 ? 's' : ''} currently on this program
            </p>
            <p style={{ color: brandColors.silver, fontSize: 13, margin: 0 }}>
              Changes you save will take effect immediately for all active clients.
            </p>
          </div>
        </div>
      )}

      {/* Draft restore banner */}
      {hasDraft && !isEditMode && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '12px',
          padding: '14px 18px', marginBottom: '20px',
          backgroundColor: 'rgba(212, 175, 55, 0.1)',
          border: '1px solid rgba(212, 175, 55, 0.4)',
          borderRadius: '8px',
        }}>
          <span style={{ color: brandColors.gold, fontSize: '14px', fontWeight: 600 }}>
            📝 You have an unsaved draft from a previous session.
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={restoreDraft}
              style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                backgroundColor: brandColors.gold, color: brandColors.darkGray,
                fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Restore Draft
            </button>
            <button
              onClick={discardDraft}
              style={{
                padding: '8px 16px', borderRadius: '8px',
                border: `1px solid ${brandColors.lightGray}`, backgroundColor: 'transparent',
                color: brandColors.silver, fontSize: '13px', cursor: 'pointer',
              }}
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: theme.danger,
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Program Details */}
      <div style={{
        backgroundColor: brandColors.mediumGray,
        padding: '25px',
        borderRadius: '8px',
        marginBottom: '30px',
        border: `1px solid ${brandColors.lightGray}`
      }}>
        <h2 style={{ color: brandColors.gold, fontSize: '20px', marginBottom: '20px' }}>
          Program Details
        </h2>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            color: brandColors.silver,
            fontSize: '14px',
            marginBottom: '8px'
          }}>
            Program Name *
          </label>
          <input
            type="text"
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            placeholder="e.g., Strength Building Phase 1"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: brandColors.darkGray,
              border: `1px solid ${brandColors.lightGray}`,
              borderRadius: '8px',
              color: 'white',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            color: brandColors.silver,
            fontSize: '14px',
            marginBottom: '8px'
          }}>
            Description
          </label>
          <textarea
            value={programDescription}
            onChange={(e) => setProgramDescription(e.target.value)}
            placeholder="Brief description of the program goals and focus..."
            rows={3}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: brandColors.darkGray,
              border: `1px solid ${brandColors.lightGray}`,
              borderRadius: '8px',
              color: 'white',
              fontSize: '16px',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Template toggle — templates go into the shared library and are used for auto-matching */}
        <div style={{ marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => setIsTemplate(v => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: isTemplate ? 'rgba(212,175,55,0.1)' : 'transparent',
              border: `1px solid ${isTemplate ? brandColors.gold : brandColors.lightGray}`,
              borderRadius: '8px',
              padding: '12px 16px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
              background: isTemplate ? brandColors.gold : 'transparent',
              border: `2px solid ${isTemplate ? brandColors.gold : brandColors.silver}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isTemplate && <span style={{ color: '#000', fontSize: '13px', fontWeight: 700 }}>✓</span>}
            </div>
            <div>
              <div style={{ color: isTemplate ? brandColors.gold : 'white', fontSize: '14px', fontWeight: 600 }}>
                Add to Template Library
              </div>
              <div style={{ color: brandColors.silver, fontSize: '12px', marginTop: '2px' }}>
                Templates are shared across all coaches and used for auto-matching new clients
              </div>
            </div>
          </button>
        </div>

      </div>

      {/* Weeks */}
      <div style={{ marginBottom: '30px' }}>
        {weeks.map((week, weekIndex) => (
          <div
            key={week.id}
            style={{
              backgroundColor: brandColors.mediumGray,
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '15px',
              border: `1px solid ${brandColors.lightGray}`
            }}
          >
            {/* Week Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: expandedWeek === week.id ? '20px' : '0'
            }}>
              <h3
                onClick={() => setExpandedWeek(expandedWeek === week.id ? null : week.id)}
                style={{
                  color: brandColors.gold,
                  fontSize: '18px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  margin: 0
                }}
              >
                {expandedWeek === week.id ? '▼' : '▶'} {week.name}
              </h3>
              <button
                onClick={() => removeWeek(week.id)}
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.danger}`,
                  color: theme.danger,
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Remove Week
              </button>
            </div>

            {/* Week Content */}
            {expandedWeek === week.id && (
              <div>
                {/* Days */}
                {week.days.map((day, dayIndex) => (
                  <div
                    key={day.id}
                    style={{
                      backgroundColor: brandColors.darkGray,
                      padding: '15px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      border: `1px solid ${brandColors.lightGray}`
                    }}
                  >
                    {/* Day Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: expandedDay === day.id ? '15px' : '0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                        <span
                          onClick={() => setExpandedDay(expandedDay === day.id ? null : day.id)}
                          style={{ color: brandColors.gold, cursor: 'pointer', fontSize: 16, userSelect: 'none', flexShrink: 0 }}
                        >
                          {expandedDay === day.id ? '▼' : '▶'}
                        </span>
                        <input
                          value={day.name}
                          onChange={(e) => updateDay(week.id, day.id, 'name', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderBottom: '1px solid transparent',
                            color: 'white',
                            fontSize: 15,
                            fontWeight: 700,
                            outline: 'none',
                            minWidth: 80,
                            width: `${Math.max(day.name.length, 6)}ch`,
                          }}
                          onFocus={(e) => e.target.style.borderBottomColor = brandColors.gold}
                          onBlur={(e) => e.target.style.borderBottomColor = 'transparent'}
                        />
                        <span style={{ color: brandColors.silver, fontSize: 12, flexShrink: 0 }}>
                          {day.blocks.reduce((s, b) => s + b.items.length, 0)} exercises
                        </span>
                      </div>
                      <button
                        onClick={() => removeDay(week.id, day.id)}
                        style={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${theme.danger}`,
                          color: theme.danger,
                          padding: '6px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          flexShrink: 0,
                        }}
                      >
                        Remove Day
                      </button>
                    </div>

                    {/* Day Content - Blocks */}
                    {expandedDay === day.id && (
                      <div>
                        {day.blocks.map((block, blockIndex) => {
                          const minExercises = getMinExercisesForCategory(block.set_category);
                          const maxExercises = getMaxExercisesForCategory(block.set_category);
                          const canAddMore = block.items.length < maxExercises;

                          return (
                            <div
                              key={block.id}
                              style={{
                                backgroundColor: brandColors.mediumGray,
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                border: `2px solid ${block.items.length >= minExercises && block.items.length <= maxExercises ? brandColors.gold : theme.danger}`
                              }}
                            >
                              {/* Block Header */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <span
                                    onClick={() => setExpandedBlock(expandedBlock === block.id ? null : block.id)}
                                    style={{
                                      color: brandColors.gold,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      fontSize: '14px'
                                    }}
                                  >
                                    {expandedBlock === block.id ? '▼' : '▶'} Block {blockIndex + 1}
                                  </span>
                                  <span style={{
                                    backgroundColor: brandColors.gold,
                                    color: brandColors.darkGray,
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 700
                                  }}>
                                    {SET_CATEGORIES[block.set_category]?.icon} {SET_CATEGORIES[block.set_category]?.label}
                                  </span>
                                  <span style={{
                                    color: brandColors.silver,
                                    fontSize: '11px'
                                  }}>
                                    {block.items.length}/{minExercises === maxExercises ? minExercises : `${minExercises}-${maxExercises}`} exercises
                                  </span>
                                </div>
                                <button
                                  onClick={() => removeBlock(week.id, day.id, block.id)}
                                  style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: theme.danger,
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 700
                                  }}
                                >
                                  ✕
                                </button>
                              </div>

                              {/* Block Configuration */}
                              {expandedBlock === block.id && (
                                <div>
                                  <div style={{
                                    backgroundColor: brandColors.darkGray,
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '12px'
                                  }}>
                                    <BlockTypeSelector
                                      value={block.set_category}
                                      onChange={(category) => updateBlock(week.id, day.id, block.id, 'set_category', category)}
                                    />
                                    <BlockConfigPanel
                                      category={block.set_category}
                                      params={block.category_params}
                                      onChange={(params) => updateBlock(week.id, day.id, block.id, 'category_params', params)}
                                    />

                                    <div style={{ marginTop: '12px' }}>
                                      <label style={{
                                        display: 'block',
                                        color: brandColors.silver,
                                        fontSize: '11px',
                                        marginBottom: '6px'
                                      }}>
                                        Rest Between Sets (seconds)
                                      </label>
                                      <input
                                        type="number"
                                        value={block.rest_seconds}
                                        onChange={(e) => updateBlock(week.id, day.id, block.id, 'rest_seconds', parseInt(e.target.value) || 60)}
                                        style={{
                                          width: '100px',
                                          padding: '8px',
                                          backgroundColor: brandColors.mediumGray,
                                          border: `1px solid ${brandColors.lightGray}`,
                                          borderRadius: '4px',
                                          color: 'white',
                                          fontSize: '14px'
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {/* Quick Presets */}
                                  {block.items.length > 0 && (
                                    <div style={{ marginTop: 12 }}>
                                      <label style={{ display: 'block', color: brandColors.silver, fontSize: 11, marginBottom: 6, fontWeight: 600 }}>
                                        Quick Preset — applies sets, reps &amp; rest to all exercises in this block
                                      </label>
                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {Object.entries(BLOCK_PRESETS).map(([key, preset]) => (
                                          <button
                                            key={key}
                                            onClick={() => applyPresetToBlock(week.id, day.id, block.id, key)}
                                            style={{
                                              padding: '6px 14px',
                                              backgroundColor: 'transparent',
                                              border: `1px solid ${brandColors.gold}`,
                                              borderRadius: 20,
                                              color: brandColors.gold,
                                              fontSize: 12,
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                            }}
                                          >
                                            {preset.label} ({preset.sets}×{preset.reps}, {preset.rest}s)
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Block Items (Exercises) */}
                                  {block.items.map((item, itemIndex) => (
                                    <div
                                      key={item.id}
                                      style={{
                                        backgroundColor: brandColors.darkGray,
                                        padding: '12px',
                                        borderRadius: '8px',
                                        marginBottom: '8px'
                                      }}
                                    >
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '10px'
                                      }}>
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}>
                                          <span style={{
                                            backgroundColor: brandColors.gold,
                                            color: brandColors.darkGray,
                                            padding: '4px 10px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 700
                                          }}>
                                            {getPositionLabel(blockIndex, item.position)}
                                          </span>
                                          <span style={{
                                            color: brandColors.white,
                                            fontWeight: 700,
                                            fontSize: '14px'
                                          }}>
                                            {item.exercise_name}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() => removeBlockItem(week.id, day.id, block.id, item.id)}
                                          style={{
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            color: theme.danger,
                                            cursor: 'pointer',
                                            fontSize: '14px'
                                          }}
                                        >
                                          ✕
                                        </button>
                                      </div>

                                      <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(4, 1fr)',
                                        gap: '10px'
                                      }}>
                                        <div>
                                          <label style={{
                                            color: brandColors.silver,
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '4px'
                                          }}>
                                            Sets
                                          </label>
                                          <input
                                            type="number"
                                            value={item.sets}
                                            onChange={(e) => updateBlockItem(week.id, day.id, block.id, item.id, 'sets', parseInt(e.target.value) || 0)}
                                            style={{
                                              width: '100%',
                                              padding: '8px',
                                              backgroundColor: brandColors.mediumGray,
                                              border: `1px solid ${brandColors.lightGray}`,
                                              borderRadius: '4px',
                                              color: 'white',
                                              fontSize: '14px',
                                              boxSizing: 'border-box'
                                            }}
                                          />
                                          {exercises.find(e => e.id === item.exercise_id)?.is_unilateral && (
                                            <div style={{ color: brandColors.gold, fontSize: '11px', marginTop: '4px' }}>
                                              Unilateral — each set logs Right + Left reps together
                                            </div>
                                          )}
                                        </div>

                                        <div>
                                          {(() => {
                                            const ex = exercises.find(e => e.id === item.exercise_id);
                                            const isTimeBased = ex?.exercise_type === 'cardio' || ex?.exercise_type === 'isometric' || ex?.exercise_type === 'stretch';
                                            const unit = ex?.duration_unit || 'minutes';
                                            return (
                                              <>
                                                <label style={{
                                                  color: brandColors.silver,
                                                  fontSize: '11px',
                                                  display: 'block',
                                                  marginBottom: '4px'
                                                }}>
                                                  {isTimeBased ? `Duration (${unit})` : 'Reps'}
                                                </label>
                                                <input
                                                  type="text"
                                                  value={item.reps}
                                                  onChange={(e) => updateBlockItem(week.id, day.id, block.id, item.id, 'reps', e.target.value)}
                                                  placeholder={isTimeBased ? (unit === 'seconds' ? '30' : '5') : '8-12'}
                                                  style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    backgroundColor: brandColors.mediumGray,
                                                    border: `1px solid ${brandColors.lightGray}`,
                                                    borderRadius: '4px',
                                                    color: 'white',
                                                    fontSize: '14px',
                                                    boxSizing: 'border-box'
                                                  }}
                                                />
                                                {isTimeBased && (
                                                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                                    {['minutes', 'seconds'].map(u => (
                                                      <button
                                                        key={u}
                                                        type="button"
                                                        onClick={() => updateExerciseDurationUnit(ex.id, u)}
                                                        style={{
                                                          flex: 1,
                                                          padding: '3px 4px',
                                                          borderRadius: '4px',
                                                          border: unit === u ? `1px solid ${brandColors.gold}` : `1px solid ${brandColors.lightGray}`,
                                                          backgroundColor: unit === u ? 'rgba(212,175,55,0.15)' : 'transparent',
                                                          color: unit === u ? brandColors.gold : brandColors.silver,
                                                          fontSize: '11px',
                                                          fontWeight: unit === u ? '700' : '400',
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        {u === 'minutes' ? 'min' : 'sec'}
                                                      </button>
                                                    ))}
                                                  </div>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </div>

                                        <div>
                                          <label style={{
                                            color: brandColors.silver,
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '4px'
                                          }}>
                                            Load %
                                          </label>
                                          <input
                                            type="text"
                                            value={item.load_percentage || ''}
                                            onChange={(e) => updateBlockItem(week.id, day.id, block.id, item.id, 'load_percentage', e.target.value)}
                                            placeholder="70%"
                                            style={{
                                              width: '100%',
                                              padding: '8px',
                                              backgroundColor: brandColors.mediumGray,
                                              border: `1px solid ${brandColors.lightGray}`,
                                              borderRadius: '4px',
                                              color: 'white',
                                              fontSize: '14px',
                                              boxSizing: 'border-box'
                                            }}
                                          />
                                        </div>

                                        <div>
                                          <label style={{
                                            color: brandColors.silver,
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '4px'
                                          }}>
                                            Notes
                                          </label>
                                          <input
                                            type="text"
                                            value={item.notes || ''}
                                            onChange={(e) => updateBlockItem(week.id, day.id, block.id, item.id, 'notes', e.target.value)}
                                            placeholder="Optional"
                                            style={{
                                              width: '100%',
                                              padding: '8px',
                                              backgroundColor: brandColors.mediumGray,
                                              border: `1px solid ${brandColors.lightGray}`,
                                              borderRadius: '4px',
                                              color: 'white',
                                              fontSize: '14px',
                                              boxSizing: 'border-box'
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                  {/* Add Exercise to Block Button */}
                                  {canAddMore && (
                                    <button
                                      onClick={() => {
                                        setShowExercisePicker({ weekId: week.id, dayId: day.id, blockId: block.id });
                                        setExerciseSearch('');
                                        setSelectedMuscleFilter('All');
                                        setSelectedSecondaryFilter('All');
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '10px',
                                        backgroundColor: 'transparent',
                                        border: `2px dashed ${brandColors.gold}`,
                                        borderRadius: '8px',
                                        color: brandColors.gold,
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        fontWeight: 700
                                      }}
                                    >
                                      + Add Exercise to Block
                                    </button>
                                  )}

                                  {!canAddMore && (
                                    <div style={{
                                      padding: '10px',
                                      textAlign: 'center',
                                      color: brandColors.silver,
                                      fontSize: '12px'
                                    }}>
                                      Block is full ({maxExercises} exercises max)
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Add Block Button */}
                        <button
                          onClick={() => addBlock(week.id, day.id)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: 'transparent',
                            border: `2px dashed ${brandColors.gold}`,
                            color: brandColors.gold,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 700
                          }}
                        >
                          + Add Block
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add Day Button */}
                <button
                  onClick={() => addDay(week.id)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'transparent',
                    border: `2px dashed ${brandColors.gold}`,
                    color: brandColors.gold,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 700
                  }}
                >
                  + Add Day
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Week, after the weeks. A new program starts with a short cue. */}
      {weeks.length === 0 && (
        <p style={{ color: brandColors.silver, fontSize: 14, textAlign: 'center', margin: '0 0 12px' }}>
          Start with week 1: add a week, then its days and exercises.
        </p>
      )}
      <button
        onClick={addWeek}
        style={{
          width: '100%',
          padding: '15px',
          backgroundColor: 'transparent',
          border: `2px dashed ${brandColors.gold}`,
          color: brandColors.gold,
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 700,
          marginBottom: '30px'
        }}
      >
        {weeks.length === 0 ? '+ Add your first week' : '+ Add Week'}
      </button>

      {/* Exercise Picker Modal */}
      {showExercisePicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: brandColors.mediumGray,
            borderRadius: '14px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: `1px solid ${brandColors.lightGray}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ color: brandColors.gold, margin: 0, fontSize: '18px' }}>
                Select Exercise
              </h3>
              <button
                onClick={() => setShowExercisePicker(null)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: brandColors.silver,
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            {/* Search and Filter */}
            <div style={{ padding: '15px', borderBottom: `1px solid ${brandColors.lightGray}` }}>
              <input
                type="text"
                placeholder="Search exercises..."
                value={exerciseSearch}
                onChange={(e) => setExerciseSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: brandColors.darkGray,
                  border: `1px solid ${brandColors.lightGray}`,
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  marginBottom: '10px',
                  boxSizing: 'border-box'
                }}
              />
              {/* Movement Category Filter */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ color: brandColors.silver, fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                  Movement Category
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {getMovementCategories().map(category => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedMuscleFilter(category);
                        setSelectedSecondaryFilter('All');
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: selectedMuscleFilter === category ? brandColors.gold : brandColors.darkGray,
                        border: `1px solid ${brandColors.lightGray}`,
                        borderRadius: '20px',
                        color: selectedMuscleFilter === category ? brandColors.darkGray : brandColors.silver,
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: selectedMuscleFilter === category ? 'bold' : 'normal'
                      }}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
              {/* Secondary Muscle Filter */}
              <div>
                <label style={{ color: brandColors.silver, fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                  Secondary Muscle
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxHeight: '80px', overflowY: 'auto' }}>
                  {getSecondaryMuscles().map(muscle => (
                    <button
                      key={muscle}
                      onClick={() => setSelectedSecondaryFilter(muscle)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: selectedSecondaryFilter === muscle ? brandColors.gold : brandColors.darkGray,
                        border: `1px solid ${brandColors.lightGray}`,
                        borderRadius: '20px',
                        color: selectedSecondaryFilter === muscle ? brandColors.darkGray : brandColors.silver,
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: selectedSecondaryFilter === muscle ? 'bold' : 'normal'
                      }}
                    >
                      {muscle}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Exercise List */}
            <div style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '10px'
            }}>
              {getFilteredExercises().length === 0 ? (
                <div style={{ textAlign: 'center', color: brandColors.silver, padding: '20px' }}>
                  No exercises found. Try a different search or filter.
                </div>
              ) : (
                getFilteredExercises().map(ex => (
                  <div
                    key={ex.id}
                    onClick={() => addExerciseToBlock(
                      showExercisePicker.weekId,
                      showExercisePicker.dayId,
                      showExercisePicker.blockId,
                      ex.id
                    )}
                    style={{
                      padding: '12px 15px',
                      backgroundColor: brandColors.darkGray,
                      borderRadius: '8px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      border: `1px solid ${brandColors.lightGray}`,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = brandColors.gold;
                      e.currentTarget.style.backgroundColor = brandColors.lightGray;
                      setHoveredExerciseId(ex.id);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = brandColors.lightGray;
                      e.currentTarget.style.backgroundColor = brandColors.darkGray;
                      setHoveredExerciseId(null);
                    }}
                    onTouchStart={() => setHoveredExerciseId(ex.id)}
                  >
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      {/* GIF thumbnail — only fetched once the row is hovered/tapped, so opening
                          the picker doesn't pull the full-size animation for every exercise at once. */}
                      {ex.gif_url && hoveredExerciseId === ex.id ? (
                        <ExerciseDemo
                          gifUrl={ex.gif_url}
                          alt={ex.name}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            flexShrink: 0,
                            transition: 'width 0.2s, height 0.2s',
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '48px', height: '48px', borderRadius: '8px',
                          backgroundColor: brandColors.lightGray, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '20px',
                        }}>
                          {ex.gif_url ? '▶' : '💪'}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'white', fontWeight: 700, marginBottom: '4px' }}>
                      {ex.name}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {ex.movement_category && (
                        <span style={{
                          backgroundColor: brandColors.gold,
                          color: brandColors.darkGray,
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: 700
                        }}>
                          {ex.movement_category}
                        </span>
                      )}
                      {ex.secondary_muscles && (
                        <span style={{
                          backgroundColor: brandColors.lightGray,
                          color: brandColors.silver,
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '11px'
                        }}>
                          {ex.secondary_muscles}
                        </span>
                      )}
                      {ex.equipment && (
                        <span style={{
                          backgroundColor: brandColors.lightGray,
                          color: brandColors.silver,
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '11px'
                        }}>
                          {ex.equipment}
                        </span>
                      )}
                    </div>
                      </div>{/* end flex-1 text col */}
                    </div>{/* end row with GIF */}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramBuilder;
